import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MessageCircle, Sparkles, Star, Check,
  ChevronLeft, ChevronRight, Truck, RefreshCw, Shield, Instagram
} from 'lucide-react';
import Layout from '../components/layout/Layout';
import { getProductById, COLLECTIONS_LIST, OCCASIONS_LIST } from '../data/products';
import { trackProductView, trackWhatsAppClick } from '../lib/analytics';

const WHATSAPP = '918889616972';
const INSTAGRAM = 'aura_printiqo';

const ProductDetailPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    const p = getProductById(productId);
    setProduct(p);
    if (p) {
      setSelectedColor(p.colors?.[0] || null);
      trackProductView(p.id, p.name);
    }
  }, [productId]);

  if (!product) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Product not found</h2>
            <Link to="/shop" className="text-[hsl(350,70%,55%)] font-medium hover:underline">← Back to Shop</Link>
          </div>
        </div>
      </Layout>
    );
  }

  const collection = COLLECTIONS_LIST.find(c => c.id === product.collection);
  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  const handleWhatsAppOrder = () => {
    trackWhatsAppClick('product_detail');
    const sizeInfo = selectedSize ? `\nSize: ${selectedSize}` : '';
    const colorInfo = selectedColor ? `\nColor: ${selectedColor.name}` : '';
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to order:\n*${product.name}*\nPrice: ₹${product.price}${sizeInfo}${colorInfo}\n\nPlease confirm availability and next steps!`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const handleCustomise = () => {
    trackWhatsAppClick('product_customise');
    const msg = `Hi AuraPrintiqo! 👋\n\nI want to *customise* this design:\n*${product.name}*\n\nI'd like to add my own photo to it. Can you guide me on how to get started?`;
    window.open(`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
  };

  const handleInstagram = () => {
    window.open(`https://ig.me/m/${INSTAGRAM}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/shop" className="hover:text-[hsl(350,70%,55%)] transition-colors flex items-center gap-1">
            <ArrowLeft className="w-3.5 h-3.5" /> Shop
          </Link>
          <span>/</span>
          <span className="text-gray-400">{collection?.label}</span>
          <span>/</span>
          <span className="text-gray-800 font-medium truncate">{product.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* ── Image Gallery ── */}
          <div>
            {/* Main image */}
            <div className="relative rounded-3xl overflow-hidden bg-gray-50 shadow-soft-lg mb-4"
              style={{ aspectRatio: '1/1' }}>
              <img
                src={product.images[imgIdx]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.badge && (
                  <span className="px-3 py-1 text-xs font-bold rounded-xl text-white shadow"
                    style={{ background: 'linear-gradient(135deg,hsl(350,70%,65%),hsl(20,90%,70%))' }}>
                    {product.badge}
                  </span>
                )}
                {discount && (
                  <span className="px-3 py-1 text-xs font-bold rounded-xl bg-green-500 text-white shadow">
                    {discount}% OFF
                  </span>
                )}
              </div>
              {/* Nav arrows */}
              {product.images.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(i => Math.max(0, i - 1))}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors">
                    <ChevronLeft className="w-4 h-4 text-gray-700" />
                  </button>
                  <button onClick={() => setImgIdx(i => Math.min(product.images.length - 1, i + 1))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/90 rounded-full flex items-center justify-center shadow hover:bg-white transition-colors">
                    <ChevronRight className="w-4 h-4 text-gray-700" />
                  </button>
                </>
              )}
            </div>
            {/* Thumbnails */}
            <div className="flex gap-3">
              {product.images.map((src, i) => (
                <button key={i} onClick={() => setImgIdx(i)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all flex-shrink-0 ${
                    i === imgIdx ? 'border-[hsl(350,70%,65%)] scale-105' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* ── Product Info ── */}
          <div>
            {/* Collection tag */}
            <Link to={`/shop?collection=${product.collection}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-[hsl(350,70%,55%)] bg-[hsl(350,70%,65%)]/10 px-3 py-1.5 rounded-full mb-4 hover:bg-[hsl(350,70%,65%)]/20 transition-colors">
              {collection?.emoji} {collection?.label}
            </Link>

            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{product.name}</h1>

            {/* Rating placeholder */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex gap-0.5">
                {[1,2,3,4,5].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
              </div>
              <span className="text-sm text-gray-500">5.0 · 50+ orders</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className="text-3xl font-bold text-gray-900">₹{product.price}</span>
              {product.originalPrice && (
                <>
                  <span className="text-lg text-gray-400 line-through">₹{product.originalPrice}</span>
                  <span className="text-sm font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-lg">
                    Save ₹{product.originalPrice - product.price}
                  </span>
                </>
              )}
            </div>

            {/* Color Picker */}
            {product.colors?.length > 0 && (
              <div className="mb-5">
                <p className="text-sm font-semibold text-gray-800 mb-2">
                  Color: <span className="font-normal text-gray-600">{selectedColor?.name}</span>
                </p>
                <div className="flex gap-2 flex-wrap">
                  {product.colors.map(c => (
                    <button
                      key={c.name}
                      onClick={() => setSelectedColor(c)}
                      title={c.name}
                      className={`w-9 h-9 rounded-full border-2 transition-all hover:scale-110 relative ${
                        selectedColor?.name === c.name ? 'border-[hsl(350,70%,55%)] scale-110' : 'border-gray-200'
                      }`}
                      style={{ backgroundColor: c.hex }}
                    >
                      {selectedColor?.name === c.name && (
                        <Check className={`w-4 h-4 absolute inset-0 m-auto ${
                          c.hex === '#FFFFFF' || c.hex === '#fde68a' || c.hex === '#f9a8d4'
                            ? 'text-gray-700' : 'text-white'
                        }`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Size Picker */}
            {product.sizes?.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-gray-800">
                    Size: <span className="font-normal text-gray-600">{selectedSize || 'Select a size'}</span>
                  </p>
                  <Link to="/size-guide" className="text-xs text-[hsl(350,70%,55%)] hover:underline">Size Guide →</Link>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {product.sizes.map(s => (
                    <button
                      key={s}
                      onClick={() => setSelectedSize(s)}
                      className={`w-12 h-12 rounded-xl border-2 text-sm font-semibold transition-all ${
                        selectedSize === s
                          ? 'border-[hsl(350,70%,65%)] bg-[hsl(350,70%,65%)]/10 text-[hsl(350,70%,55%)]'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3 mb-6">
              <button
                onClick={handleWhatsAppOrder}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl text-white font-bold text-base shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{ background: 'linear-gradient(135deg,#25D366,#128C7E)' }}
              >
                <MessageCircle className="w-5 h-5" />
                Order via WhatsApp
              </button>

              {product.customisable && (
                <button
                  onClick={handleCustomise}
                  className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-bold text-base border-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    borderColor: 'hsl(350,70%,65%)',
                    color: 'hsl(350,70%,55%)',
                    background: 'hsl(350,70%,65%)/5',
                  }}
                >
                  <Sparkles className="w-5 h-5" />
                  Customise with My Photo
                </button>
              )}

              <button
                onClick={handleInstagram}
                className="w-full flex items-center justify-center gap-3 py-3 rounded-2xl text-white font-semibold text-sm"
                style={{ background: 'linear-gradient(135deg,#833AB4,#FD1D1D,#F77737)' }}
              >
                <Instagram className="w-4 h-4" />
                Order via Instagram
              </button>
            </div>

            {/* Trust row */}
            <div className="flex items-center gap-4 text-xs text-gray-500 py-4 border-y border-gray-100">
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-green-500" />5–7 day delivery</span>
              <span className="flex items-center gap-1.5"><RefreshCw className="w-4 h-4 text-blue-500" />2 free revisions</span>
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-purple-500" />Secure payment</span>
            </div>

            {/* Tabs */}
            <div className="mt-5">
              <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-4 w-fit">
                {['details', 'sizing', 'faq'].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
                      activeTab === tab ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}>
                    {tab === 'faq' ? 'FAQ' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>

              {activeTab === 'details' && (
                <div className="text-sm text-gray-600 leading-relaxed space-y-3">
                  <p>{product.description}</p>
                  <ul className="space-y-1.5">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {product.type === 'hoodie' ? '320 GSM Cotton Fleece Blend' : '180 GSM Combed Cotton'}
                    </li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" />DTG Premium Printing</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" />Wash-safe, long-lasting print</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" />AI-generated custom artwork</li>
                    {product.occasion?.length > 0 && (
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        Perfect for: {product.occasion.map(o => OCCASIONS_LIST.find(ol => ol.id === o)?.label).join(', ')}
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {activeTab === 'sizing' && (
                <div className="text-sm text-gray-600">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700 rounded-l-lg">Size</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700">Chest (in)</th>
                        <th className="text-left py-2 px-3 text-xs font-semibold text-gray-700 rounded-r-lg">Length (in)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {[
                        { size: 'S', chest: '36', length: '26' },
                        { size: 'M', chest: '38', length: '27' },
                        { size: 'L', chest: '40', length: '28' },
                        { size: 'XL', chest: '42', length: '29' },
                        { size: 'XXL', chest: '44', length: '30' },
                      ].map(row => (
                        <tr key={row.size} className={selectedSize === row.size ? 'bg-[hsl(350,70%,65%)]/5' : ''}>
                          <td className="py-2 px-3 font-medium text-gray-800">{row.size}</td>
                          <td className="py-2 px-3 text-gray-600">{row.chest}"</td>
                          <td className="py-2 px-3 text-gray-600">{row.length}"</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {activeTab === 'faq' && (
                <div className="space-y-3 text-sm text-gray-600">
                  {[
                    { q: 'Can I customise this design with my photo?', a: 'Yes! Click "Customise with My Photo" and we\'ll transform your photo into anime art matching this design style.' },
                    { q: 'How long does delivery take?', a: 'T-shirts are delivered in 5–7 business days. Hoodies take 7–10 business days after artwork approval.' },
                    { q: 'Can I request changes to the design?', a: 'Yes — you get up to 2 free revisions on your custom artwork before we print.' },
                    { q: 'What if I don\'t like the result?', a: 'We share a preview before printing. If there\'s a quality defect in the final product, we\'ll replace it.' },
                  ].map((item, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4">
                      <p className="font-semibold text-gray-800 mb-1">{item.q}</p>
                      <p>{item.a}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetailPage;
